import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { wsClient } from '../../services/websocket-client.js';
import type { TerminalCreatedMessage, TerminalOutputMessage, TerminalErrorMessage } from '../../types/project.js';

@customElement('cp-breakdown-terminal')
export class CpBreakdownTerminal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) sessionId = '';
  @property({ type: String }) breakdownLevel = '';

  @state() private _connected = false;
  @state() private _error = '';

  private _terminal: Terminal | null = null;
  private _fitAddon: FitAddon | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _wsHandler = this._onWsMessage.bind(this);
  private _initialized = false;
  private _autoLaunched = false;
  private _readyTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
    this._dispose();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('sessionId') && this.sessionId) {
      this._connected = true;
      this._error = '';
      // Deferred init: double requestAnimationFrame ensures DOM is ready
      if (!this._initialized) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._initTerminal();
          });
        });
      }
    }
  }

  private _initTerminal(): void {
    const container = this.querySelector('.cp-breakdown-terminal__xterm');
    if (!container || this._initialized) return;

    // Retry if container has zero dimensions (not yet visible, e.g. inside a modal)
    if ((container as HTMLElement).offsetWidth === 0 || (container as HTMLElement).offsetHeight === 0) {
      setTimeout(() => this._initTerminal(), 100);
      return;
    }

    this._terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontSize: 14,
      lineHeight: 1.5,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      theme: {
        background: '#0F1F33',
        foreground: '#E8EDF2',
        cursor: '#00D4FF',
        selectionBackground: '#1E3A5F',
        black: '#0B1929',
        red: '#EF4444',
        green: '#22C55E',
        yellow: '#F59E0B',
        blue: '#00D4FF',
        magenta: '#A855F7',
        cyan: '#06B6D4',
        white: '#E8EDF2',
        brightBlack: '#64748B',
        brightRed: '#F87171',
        brightGreen: '#4ADE80',
        brightYellow: '#FBBF24',
        brightBlue: '#33DDFF',
        brightMagenta: '#C084FC',
        brightCyan: '#22D3EE',
        brightWhite: '#FFFFFF',
      },
      scrollback: 5000,
      convertEol: true,
      allowProposedApi: true,
    });

    this._fitAddon = new FitAddon();
    this._terminal.loadAddon(this._fitAddon);

    this._terminal.open(container as HTMLElement);
    this._fitAddon.fit();

    // Send initial resize to backend PTY so cols/rows match
    this._sendResize();

    // Send user input to PTY via WebSocket
    this._terminal.onData((data: string) => {
      if (this.sessionId) {
        wsClient.send({
          type: 'terminal:input',
          payload: { sessionId: this.sessionId, data },
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Observe container size changes for responsive fit + backend resize sync
    this._resizeObserver = new ResizeObserver(() => {
      if (!this._fitAddon || !this._terminal) return;

      const el = container as HTMLElement;
      if (el.offsetWidth === 0 || el.offsetHeight === 0) return;

      this._fitAddon.fit();
      this._sendResize();
    });
    this._resizeObserver.observe(container);

    this._initialized = true;
  }

  private _sendResize(): void {
    if (!this._terminal || !this.sessionId) return;
    const cols = this._terminal.cols;
    const rows = this._terminal.rows;
    wsClient.send({
      type: 'terminal:resize',
      payload: { sessionId: this.sessionId, cols, rows },
      timestamp: new Date().toISOString(),
    });
  }

  private _onWsMessage(e: CustomEvent): void {
    const msg = e.detail as { type: string; payload: unknown };

    switch (msg.type) {
      case 'terminal:created': {
        const payload = msg.payload as TerminalCreatedMessage;
        if (payload.sessionId === this.sessionId) {
          this._connected = true;
          this._error = '';
        }
        break;
      }

      case 'terminal:output': {
        const payload = msg.payload as TerminalOutputMessage;
        if (payload.sessionId === this.sessionId && this._terminal) {
          this._terminal.write(payload.data);
          this._tryAutoLaunch();
        }
        break;
      }

      case 'terminal:error': {
        const payload = msg.payload as TerminalErrorMessage;
        if (payload.sessionId === this.sessionId) {
          this._error = payload.error;
        }
        break;
      }

      case 'terminal:exited': {
        const payload = msg.payload as { sessionId: string };
        if (payload.sessionId === this.sessionId) {
          this._connected = false;
          this._terminal?.write('\r\n\x1b[33m[Session ended]\x1b[0m\r\n');
        }
        break;
      }

      case 'terminal:closed': {
        const payload = msg.payload as { sessionId: string };
        if (payload.sessionId === this.sessionId) {
          this._connected = false;
        }
        break;
      }
    }
  }

  private _getSkillCommand(): string {
    const commands: Record<string, string> = {
      monthly: '/breakdown-monthly',
      weekly: '/breakdown-weekly',
      daily: '/breakdown-daily',
      'add-team-member': '/add-team-member',
    };
    return commands[this.breakdownLevel] || '';
  }

  private _tryAutoLaunch(): void {
    if (this._autoLaunched || !this.breakdownLevel || !this.sessionId) return;

    const command = this._getSkillCommand();
    if (!command) return;

    // Debounce: reset timer on every output chunk. When output stops
    // arriving for 800ms, Claude CLI is likely at its prompt and ready.
    if (this._readyTimer) clearTimeout(this._readyTimer);
    this._readyTimer = setTimeout(() => {
      if (this._autoLaunched) return;
      this._autoLaunched = true;
      wsClient.send({
        type: 'terminal:input',
        payload: { sessionId: this.sessionId, data: command + '\n' },
        timestamp: new Date().toISOString(),
      });
    }, 800);
  }

  private _dispose(): void {
    if (this._readyTimer) {
      clearTimeout(this._readyTimer);
      this._readyTimer = null;
    }
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._terminal?.dispose();
    this._terminal = null;
    this._fitAddon = null;
    this._initialized = false;
    this._autoLaunched = false;
  }

  render() {
    return html`
      <div class="cp-breakdown-terminal">
        ${this._error ? html`
          <div class="cp-breakdown-terminal__error">${this._error}</div>
        ` : nothing}
        ${!this.sessionId ? html`
          <div class="cp-breakdown-terminal__placeholder">
            <span>Terminal wartet auf Session...</span>
          </div>
        ` : html`
          <div class="cp-breakdown-terminal__xterm"></div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-breakdown-terminal': CpBreakdownTerminal;
  }
}
